/**
 * School-account read-only card for Settings > My-School (Plan 13).
 *
 * Surfaces the Ordo-provisioned config that SCHOOL_ADMIN cannot edit
 * directly — school name, organization, time zone, configured grade
 * ranges, federal-program enrollment, CEP designation, school-account
 * coverage. Each read-only row has a "Request a change" CTA that
 * opens the `RequestChangeModal` and creates a Plan-27 BlockingTask.
 *
 * Schema gap (deferred to a follow-up plan):
 *   - Federal-program enrollment (`SchoolMealProgram` rows) is not yet
 *     exposed on the AdminSchool wire shape. We surface a placeholder
 *     row with "Not yet provisioned" so the Request-a-change CTA is
 *     still wired and the page communicates the intent.
 *   - CEP designation + school-account coverage live on a TBD
 *     `LocationAccountCoverage` table per Plan 13's "School-account
 *     coverage" Open Question. Same treatment: placeholder + CTA.
 *
 * The CTA itself works today for any field the admin clicks — the
 * BlockingTask record carries the field name as a free-form string,
 * so adding new field rows in this card later just means adding new
 * <ReadOnlyRow ... /> entries; the server side is field-agnostic.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Building2, Pencil } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { type AdminSchool, gradeRangeColumnLabel } from '@/api/school-orders'

import { CardTitleRow } from './campus-scope'
import { RequestChangeModal } from './request-change-modal'

export interface SchoolAccountCardProps {
  school: AdminSchool
  /**
   * The section's campus picker (`CampusScope`), rendered in this card's
   * header. Absent for a single-campus org — then the header renders exactly
   * as it did before per-card scoping.
   */
  scopeControl?: ReactNode
}

interface PendingChangeRequest {
  field: string
  fieldLabel: string
  currentValue: string | null
}

export function SchoolAccountCard({ school, scopeControl }: SchoolAccountCardProps) {
  const [pending, setPending] = useState<PendingChangeRequest | null>(null)

  const orderedGradeRanges = [...school.gradeRanges]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(gradeRangeColumnLabel)
    .join(', ')

  function openRequest(field: string, fieldLabel: string, currentValue: string | null) {
    setPending({ field, fieldLabel, currentValue })
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitleRow scopeControl={scopeControl}>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4" />
              School account
            </CardTitle>
          </CardTitleRow>
          <p className="text-sm text-muted-foreground">
            Details of your school's Ordo account. Use "Request a change" and our team will take care of it.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-1">
          <ReadOnlyRow
            label="School name"
            value={school.name}
            onRequest={() => openRequest('schoolName', 'School name', school.name)}
          />
          <ReadOnlyRow
            label="Organization"
            value={school.organization.name}
            onRequest={() => openRequest('organizationName', 'Organization', school.organization.name)}
          />
          <ReadOnlyRow
            label="Time zone"
            value={school.timeZone}
            onRequest={() => openRequest('timeZone', 'Time zone', school.timeZone)}
          />
          <ReadOnlyRow
            label="Grade ranges"
            value={orderedGradeRanges || '— none configured —'}
            helpText="Managed by your Ordo team along with the grade ranges above."
            onRequest={() => openRequest('gradeRanges', 'Grade ranges (structural)', orderedGradeRanges || null)}
          />
          <ReadOnlyRow
            label="Federal program enrollment"
            value={null}
            placeholder={
              <Badge variant="outline" className="text-xs">
                Not set up yet
              </Badge>
            }
            helpText="NSLP / SBP / CACFP / SFSP / SSO participation, managed by your Ordo team."
            onRequest={() => openRequest('federalProgramEnrollment', 'Federal program enrollment', null)}
          />
          <ReadOnlyRow
            label="CEP designation"
            value={null}
            placeholder={
              <Badge variant="outline" className="text-xs">
                Not set up yet
              </Badge>
            }
            helpText="Community Eligibility Provision status for your school."
            onRequest={() => openRequest('cepDesignation', 'CEP designation', null)}
          />
          <ReadOnlyRow
            label="School-account coverage"
            value={null}
            placeholder={
              <Badge variant="outline" className="text-xs">
                Not set up yet
              </Badge>
            }
            helpText="Which items your school's account pays for and which are billed to families."
            onRequest={() => openRequest('accountCoverage', 'School-account coverage', null)}
          />
        </CardContent>
      </Card>

      {pending ? (
        <RequestChangeModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setPending(null)
            }
          }}
          locationId={school.id}
          field={pending.field}
          fieldLabel={pending.fieldLabel}
          currentValue={pending.currentValue}
        />
      ) : null}
    </>
  )
}

function ReadOnlyRow({
  label,
  value,
  placeholder,
  helpText,
  onRequest,
}: {
  label: string
  value: string | null
  placeholder?: React.ReactNode
  helpText?: string
  onRequest: () => void
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b py-3 last:border-b-0">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        {value !== null ? (
          <span className="truncate text-sm font-medium">{value}</span>
        ) : (
          <span className="text-sm font-medium text-muted-foreground">{placeholder ?? '—'}</span>
        )}
        {helpText ? <span className="text-xs text-muted-foreground">{helpText}</span> : null}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={onRequest} className="shrink-0">
        <Pencil className="mr-1 h-3 w-3" />
        Request a change
      </Button>
    </div>
  )
}
